import { Component, Inject, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { AdditionalLinksValue } from "@liebherr/patternlib/dist/types/components/footer/patternlib-footer/patternlib-footer";
import { Language, UserAvatar } from "@liebherr/patternlib-angular";
import LiebherrAuth from "@liebherr/spa-auth";
import { TranslateService } from "@ngx-translate/core";
import { Profile, User } from "oidc-client";

import { FullNamePipe } from "./pipes/full-name.pipe";
import { InitialsPipe } from "./pipes/initials.pipe";
import { AppConfigService } from "./services/app-config.service";

@Component({
  selector: "lh-docs-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  userAvatar: UserAvatar;
  footerLinks: AdditionalLinksValue[] = [];
  user: User;
  private dashboardUrl = "";

  languages: Language[] = [
    new Language("Deutsch", "de"),
    new Language("English", "en"),
    new Language("Español", "es"),
    new Language("Français", "fr"),
    new Language("Italiano", "it"),
    new Language("Polski", "pl"),
    new Language("Pусский", "ru"),
    new Language("Türkçe", "tr"),
    new Language("简体中文", "zh"),
    new Language("日本語", "ja"),
    new Language("한국어", "ko"),
  ];

  public navlinks: Array<{
    uri: string;
    label: string;
  }> = [];

  constructor(
    @Inject("LH_AUTH") private auth: LiebherrAuth,
    @Inject("ENVIRONMENT_NAME") public environmentName: string,
    private translateService: TranslateService,
    private initialsPipe: InitialsPipe,
    private fullNamePipe: FullNamePipe,
    private titleService: Title,
    private appConfigService: AppConfigService,
  ) {
    if (!auth.user) {
      throw new ReferenceError();
    }

    this.user = auth.user;

    this.userAvatar = this.buildUserAvatar(auth.user.profile);
  }

  ngOnInit(): void {
    this.translateService
      .stream(["portal_name", "imprint", "privacy_policy", "terms_of_use"])
      .subscribe((x) => {
        this.titleService.setTitle(x["portal_name"] + " | MyLiebherr");

        this.footerLinks = [
          {
            itemId: "imprint",
            label: x["imprint"],
            to: "https://go.liebherr.com/81duXN",
            target: "_blank",
          },
          {
            itemId: "privacy-policy",
            label: x["privacy_policy"],
            to: "https://go.liebherr.com/4A6w7G",
            target: "_blank",
          },
          {
            itemId: "terms-of-use",
            label: x["terms_of_use"],
            to: "https://go.liebherr.com/bkr321",
            target: "_blank",
          },
        ];
      });

    this.appConfigService.getAppConfig().then((x) => {
      this.dashboardUrl = x.myLiebherrDashboardUrl;

      this.navlinks = [
        {
          uri: `${x.profileManagementUrl}/personal-data`,
          label: "Personal data",
        },
        {
          uri: `${x.profileManagementUrl}/colleagues`,
          label: "Manage Colleagues",
        },
        {
          uri: `${x.profileManagementUrl}/address`,
          label: "Address Book",
        },
        {
          uri: `${x.profileManagementUrl}/business-relations`,
          label: "Business Relation",
        },
        {
          uri: `${x.profileManagementUrl}/products`,
          label: "My Products",
        },
        {
          uri: `${x.profileManagementUrl}/orders`,
          label: "Orders",
        },
      ];
    });
  }

  private buildUserAvatar(oidcProfile: Profile) {
    const profile = {
      firstName: oidcProfile.given_name ?? null,
      lastName: oidcProfile.family_name ?? null,
      displayName: oidcProfile.sub,
    };

    return {
      initials: this.initialsPipe.transform(profile),
      name: this.fullNamePipe.transform(profile),
      info: oidcProfile.company_name,
      enableLogout: true,
    };
  }

  onLanguageChange(newLangCode: string): void {
    this.translateService.use(newLangCode);
  }

  onBrandLogoClick() {
    window.location.href = this.dashboardUrl;
  }

  onLogout() {
    this.auth.logout();
  }

  get runningInIframe(): boolean {
    return window.self !== window.top;
  }
}
